import React, { useContext, useRef, useState, useEffect } from 'react';
import { PlayerContext } from '../App';
import DevotionalThumbnail from './DevotionalThumbnail';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

let ytScriptInjected = false;

export default function Player() {
  const {
    currentTrack, setCurrentTrack,
    queue, queueIdx, setQueueIdx,
    mobPlayerOpen, setMobPlayerOpen,
  } = useContext(PlayerContext);

  /* YT player lives in a manually-created div inside ytWrapRef */
  const ytWrapRef   = useRef(null);
  const ytPlayerRef = useRef(null);
  const pendingRef  = useRef(null);
  const queueRef    = useRef(queue);
  const idxRef      = useRef(queueIdx);
  const pollRef     = useRef(null);

  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [curSecs, setCurSecs]   = useState(0);
  const [totSecs, setTotSecs]   = useState(0);
  const [volume, setVolume]     = useState(50);
  const [speedIdx, setSpeedIdx] = useState(1);

  /* Keep refs in sync with React state */
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { idxRef.current   = queueIdx; }, [queueIdx]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      const p = ytPlayerRef.current;
      if (!p || typeof p.getCurrentTime !== 'function') return;
      try {
        const cur = p.getCurrentTime();
        const dur = p.getDuration();
        setCurSecs(cur);
        setTotSecs(dur);
        setProgress(dur > 0 ? (cur / dur) * 100 : 0);
      } catch {}
    }, 500);
  };

  const initYTPlayer = () => {
    if (!ytWrapRef.current || ytPlayerRef.current) return;
    /* Create a child div for YT to replace — keeps React's DOM intact */
    const div = document.createElement('div');
    ytWrapRef.current.appendChild(div);

    ytPlayerRef.current = new window.YT.Player(div, {
      height: '1',
      width:  '1',
      playerVars: { autoplay: 0, controls: 0, modestbranding: 1, rel: 0, playsinline: 1 },
      events: {
        onReady: () => {
          ytPlayerRef.current.setVolume(50);
          if (pendingRef.current) {
            ytPlayerRef.current.loadVideoById(pendingRef.current);
            pendingRef.current = null;
          }
        },
        onStateChange: (evt) => {
          const S = window.YT.PlayerState;
          if (evt.data === S.PLAYING) { setPlaying(true);  startPolling(); }
          if (evt.data === S.PAUSED)  { setPlaying(false); }
          if (evt.data === S.ENDED)   {
            setPlaying(false);
            if (pollRef.current) clearInterval(pollRef.current);
            const q   = queueRef.current;
            const idx = idxRef.current;
            if (idx < q.length - 1) {
              const next = idx + 1;
              setQueueIdx(next);
              setCurrentTrack(q[next]);
            }
          }
        },
      },
    });
  };

  /* Inject YouTube IFrame API script once globally */
  useEffect(() => {
    if (!ytScriptInjected) {
      ytScriptInjected = true;
      window.onYouTubeIframeAPIReady = initYTPlayer;
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    } else if (window.YT?.Player) {
      initYTPlayer();
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Load video when track changes */
  useEffect(() => {
    if (!currentTrack?.ytId) return;
    try {
      const prev = JSON.parse(localStorage.getItem('fm_recent') || '[]');
      const updated = [currentTrack, ...prev.filter(t => t.id !== currentTrack.id)].slice(0, 8);
      localStorage.setItem('fm_recent', JSON.stringify(updated));
    } catch {}
    setProgress(0); setCurSecs(0); setTotSecs(0);
    const p = ytPlayerRef.current;
    if (p && typeof p.loadVideoById === 'function') {
      p.loadVideoById(currentTrack.ytId);
    } else {
      pendingRef.current = currentTrack.ytId;
    }
  }, [currentTrack]);

  /* Keyboard shortcuts */
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const p = ytPlayerRef.current;
      if (!p) return;
      if (e.key === ' ') {
        e.preventDefault();
        if (playing) { p.pauseVideo(); setPlaying(false); }
        else         { p.playVideo();  setPlaying(true);  }
      }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); try { p.seekTo(Math.max(0, p.getCurrentTime() - 15), true); } catch {} }
      if (e.key === 'ArrowRight') { e.preventDefault(); try { p.seekTo(p.getCurrentTime() + 15, true); } catch {} }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [playing]);

  /* ── Derived ── */
  const hasPrev = queue.length > 1 && queueIdx > 0;
  const hasNext = queue.length > 1 && queueIdx < queue.length - 1;

  const playNext = () => {
    if (!hasNext) return;
    const next = queueIdx + 1;
    setQueueIdx(next);
    setCurrentTrack(queue[next]);
  };

  const playPrev = () => {
    if (!hasPrev) return;
    const prev = queueIdx - 1;
    setQueueIdx(prev);
    setCurrentTrack(queue[prev]);
  };

  const togglePlay = () => {
    const p = ytPlayerRef.current;
    if (!p) return;
    if (playing) { p.pauseVideo(); setPlaying(false); }
    else         { p.playVideo();  setPlaying(true);  }
  };

  const seek = (e) => {
    const p = ytPlayerRef.current;
    if (!p || !totSecs) return;
    const rect = e.currentTarget.getBoundingClientRect();
    p.seekTo(((e.clientX - rect.left) / rect.width) * totSecs, true);
  };

  const changeVolume = (e) => {
    const v = parseInt(e.target.value);
    setVolume(v);
    ytPlayerRef.current?.setVolume(v);
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    ytPlayerRef.current?.setPlaybackRate(SPEEDS[next]);
  };

  const skip = (s) => {
    try { ytPlayerRef.current?.seekTo(Math.max(0, ytPlayerRef.current.getCurrentTime() + s), true); } catch {}
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  const VolIcon = () => {
    if (volume === 0) return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.5 12A4.5 4.5 0 0014 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25A7 7 0 0112 19c-1.05 0-2.04-.25-2.92-.68L7.62 19.78A9 9 0 0012 21c1.8 0 3.46-.54 4.85-1.47L19.73 23 21 21.73 4.27 3zM12 3L9.91 5.09 12 7.18V3z"/>
      </svg>
    );
    if (volume < 50) return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.5 12A4.5 4.5 0 0016 7.97v8.05A4.5 4.5 0 0018.5 12zM5 9v6h4l5 5V4L9 9H5z"/>
      </svg>
    );
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77 0-4.28-2.99-7.86-7-8.77z"/>
      </svg>
    );
  };

  /* ─────────────── MINI PLAYER ─────────────── */
  const miniPlayer = currentTrack ? (
    <div className="player-bar">
      <div className="player-top-row">
        <div className="player-disc-wrap">
          <div className={`player-disc ${playing ? 'spinning' : ''}`}>
            <DevotionalThumbnail id={currentTrack.id} showTitle={false} />
          </div>
          {playing && <div className="player-disc-glow" />}
        </div>

        <div className="player-track-info">
          <div className="player-track-title">{currentTrack.title}</div>
          <div className="player-track-show">
            {currentTrack.showTitle}
            {queue.length > 1 && (
              <span className="player-queue-badge">{queueIdx + 1} / {queue.length}</span>
            )}
          </div>
        </div>

        <div className="player-controls">
          <button className={`ctrl-btn ${!hasPrev ? 'ctrl-btn-dim' : ''}`} onClick={playPrev} disabled={!hasPrev} title="Previous">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
          </button>
          <button className="ctrl-btn" onClick={() => skip(-15)} title="Back 15s">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>
          </button>
          <button className="ctrl-btn play-btn" onClick={togglePlay}>
            {playing
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,3 20,12 6,21"/></svg>}
          </button>
          <button className="ctrl-btn" onClick={() => skip(15)} title="Forward 15s">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/></svg>
          </button>
          <button className={`ctrl-btn ${!hasNext ? 'ctrl-btn-dim' : ''}`} onClick={playNext} disabled={!hasNext} title="Next">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z"/></svg>
          </button>
          <button className="ctrl-btn speed-btn" onClick={cycleSpeed}>{SPEEDS[speedIdx]}×</button>
        </div>

        <div className="player-vol-wrap">
          <VolIcon />
          <input type="range" min="0" max="100" step="1" value={volume} onChange={changeVolume} className="volume-slider" />
        </div>

        <button className="player-close" onClick={() => { setCurrentTrack(null); setPlaying(false); try { ytPlayerRef.current?.stopVideo(); } catch {} }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="player-seek-row">
        <span className="player-time">{fmt(curSecs)}</span>
        <div className="player-seek-bar" onClick={seek}>
          <div className="player-seek-track">
            <div className="player-seek-fill" style={{ width: `${progress}%` }}>
              <div className="player-seek-thumb" />
            </div>
          </div>
        </div>
        <span className="player-time">{fmt(totSecs)}</span>
      </div>
    </div>
  ) : null;

  /* ─────────────── FULL-SCREEN MOBILE OVERLAY ─────────────── */
  const fullPlayer = (currentTrack && mobPlayerOpen) ? (
    <div className="mob-fp-backdrop" onClick={() => setMobPlayerOpen(false)}>
      <div className="mob-fp-sheet" onClick={e => e.stopPropagation()}>
        <div className="mob-fp-handle" />

        <div className="mob-fp-toprow">
          <span className="mob-fp-label">Now Playing</span>
          <button className="mob-fp-close" onClick={() => setMobPlayerOpen(false)}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="mob-fp-art-wrap">
          <div className={`mob-fp-disc ${playing ? 'spinning' : ''}`}>
            <DevotionalThumbnail id={currentTrack.id} showTitle={false} />
          </div>
          {playing && <div className="mob-fp-glow" />}
        </div>

        <div className="mob-fp-info">
          <div className="mob-fp-title">{currentTrack.title}</div>
          <div className="mob-fp-show">
            {currentTrack.showTitle}
            {queue.length > 1 && (
              <span className="player-queue-badge">{queueIdx + 1} / {queue.length}</span>
            )}
          </div>
        </div>

        <div className="mob-fp-seek-row">
          <span className="player-time">{fmt(curSecs)}</span>
          <div className="player-seek-bar mob-fp-seek-bar" onClick={seek}>
            <div className="player-seek-track">
              <div className="player-seek-fill" style={{ width: `${progress}%` }}>
                <div className="player-seek-thumb" />
              </div>
            </div>
          </div>
          <span className="player-time">{fmt(totSecs)}</span>
        </div>

        <div className="mob-fp-controls">
          <button className={`ctrl-btn mob-fp-ctrl ${!hasPrev ? 'ctrl-btn-dim' : ''}`} onClick={playPrev} disabled={!hasPrev}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
          </button>
          <button className="ctrl-btn mob-fp-ctrl" onClick={() => skip(-15)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>
          </button>
          <button className="ctrl-btn play-btn mob-fp-play" onClick={togglePlay}>
            {playing
              ? <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              : <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,3 20,12 6,21"/></svg>}
          </button>
          <button className="ctrl-btn mob-fp-ctrl" onClick={() => skip(15)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/></svg>
          </button>
          <button className={`ctrl-btn mob-fp-ctrl ${!hasNext ? 'ctrl-btn-dim' : ''}`} onClick={playNext} disabled={!hasNext}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z"/></svg>
          </button>
        </div>

        <div className="mob-fp-extra">
          <button className="ctrl-btn speed-btn mob-fp-speed" onClick={cycleSpeed}>{SPEEDS[speedIdx]}×</button>
          <div className="player-vol-wrap mob-fp-vol">
            <VolIcon />
            <input type="range" min="0" max="100" step="1" value={volume} onChange={changeVolume} className="volume-slider" />
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Always-mounted wrapper — YT IFrame lives here, outside React's diff */}
      <div ref={ytWrapRef} style={{ position: 'fixed', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden', pointerEvents: 'none' }} />
      {miniPlayer}
      {fullPlayer}
    </>
  );
}
